import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Link as MuiLink
} from '@mui/material';
import { 
  Email, 
  Lock, 
  Visibility, 
  VisibilityOff, 
  Phone,
  Person,
  Google
} from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useRegister, usePhoneRegister } from '@/hooks/useAuth';
import { useAuth } from '@/contexts/AuthContext';
import { Layout } from '@/components/Layout';
import { GoogleSignInButton, PhoneAuthFlow } from '@/components/common';
import ReCaptcha from '@/components/common/ReCaptcha';
import { validatePhoneNumber } from '@/utils/phone-validation';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import { recaptchaService } from '@/utils/recaptcha-service';

interface RegisterForm {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
}

interface PhoneRegisterForm {
  phoneNumber: string;
  password: string;
  confirmPassword: string;
  name: string;
}

export default function Register() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const emailRegisterMutation = useRegister();
  const phoneRegisterMutation = usePhoneRegister();
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerMethod, setRegisterMethod] = useState<'email' | 'phone'>('email');
  const [showPhoneFlow, setShowPhoneFlow] = useState(false);
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState<string>('');
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  
  const recaptcha = useRecaptcha({
    onError: (error) => setError(`reCAPTCHA error: ${error}`),
    onExpired: () => setError('reCAPTCHA has expired. Please verify again.'),
  });

  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    watch: watchEmail
  } = useForm<RegisterForm>({ mode: 'onChange' });

  const {
    register: registerPhone,
    handleSubmit: handlePhoneSubmit,
    formState: { errors: phoneErrors },
    watch: watchPhone
  } = useForm<PhoneRegisterForm>({ mode: 'onChange' });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Load reCAPTCHA configuration
  useEffect(() => {
    loadRecaptchaConfig();
  }, []);

  const loadRecaptchaConfig = () => {
    try {
      const enabled = recaptchaService.isEnabled();
      setRecaptchaEnabled(enabled);
      
      if (enabled) {
        const siteKey = recaptchaService.getSiteKey();
        setRecaptchaSiteKey(siteKey);
      }
    } catch (error) {
      console.warn('Could not load reCAPTCHA config:', error);
      setRecaptchaEnabled(false);
    }
  };

  // Handle OAuth error from URL parameters
  useEffect(() => {
    const errorDescription = searchParams.get('error_description');
    if (errorDescription) {
      if (errorDescription.includes('ACCOUNT_EXISTS')) {
        // Extract email from error message if available
        const emailMatch = errorDescription.match(/email ([^\s]+)/);
        const email = emailMatch ? emailMatch[1] : '';
        
        setError(`An account with${email ? ` email ${email}` : ' this email'} already exists. Please try one of the options below.`);
      } else {
        setError(errorDescription);
      }
      
      // Clear the error from URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('error_description');
      newSearchParams.delete('error');
      navigate({ search: newSearchParams.toString() }, { replace: true });
    }
  }, [searchParams, navigate]);

  const onEmailSubmit = async (data: RegisterForm) => {
    setError(null);
    
    // Check reCAPTCHA if enabled
    if (recaptchaEnabled && !recaptcha.isVerified) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    try {
      const submitData = {
        ...data,
        userType: 'hosts' as const,
        recaptchaToken: recaptcha.token,
      };
      await emailRegisterMutation.mutateAsync(submitData);
    } catch (err: any) {
      console.error('Email registration error:', err);
      setError(err.message || 'Registration failed');
      
      // Reset reCAPTCHA on error
      if (recaptchaEnabled) {
        recaptcha.reset();
      }
    }
  };

  const onPhoneSubmit = async (data: PhoneRegisterForm) => {
    setError(null);
    
    // Check reCAPTCHA if enabled
    if (recaptchaEnabled && !recaptcha.isVerified) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    const phoneValidation = validatePhoneNumber(data.phoneNumber);
    if (!phoneValidation.isValid) {
      setError(phoneValidation.error || 'Invalid phone number format');
      return;
    }

    try {
      const submitData = {
        phoneNumber: phoneValidation.formatted!,
        password: data.password,
        confirmPassword: data.confirmPassword,
        name: data.name,
        userType: 'hosts' as const,
        recaptchaToken: recaptcha.token,
      };
      await phoneRegisterMutation.mutateAsync(submitData);
    } catch (err: any) {
      console.error('Phone registration error:', err);
      setError(err.message || 'Registration failed');
      
      // Reset reCAPTCHA on error
      if (recaptchaEnabled) {
        recaptcha.reset();
      }
    }
  };

  const isLoading = emailRegisterMutation.isLoading || phoneRegisterMutation.isLoading;

  const handlePhoneFlowSuccess = async (phoneNumber: string) => {
    try {
      // The phone number has been verified, registration is complete
      alert('Registration completed successfully! You can now sign in.');
      navigate('/auth/login');
    } catch (err: any) {
      console.error('Phone flow registration error:', err);
      setError(err.message || 'Registration failed');
    }
  };

  const handlePhoneFlowError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleGoogleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  return (
    <Layout showNavigation={false}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '70vh',
          px: 2,
        }}
      >
        <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Create Account
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Join our BnB management platform
          </Typography>

          {error && (
            <Alert 
              severity={error.includes('account') && error.includes('exists') ? 'warning' : 'error'} 
              sx={{ mb: 2 }}
            >
              {error}
              {error.includes('account') && error.includes('exists') && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Try these options:
                  </Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Google />}
                      onClick={() => navigate('/auth/login')}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      Login with Google (if previously linked)
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Email />}
                      onClick={() => navigate('/auth/login')}
                      sx={{ justifyContent: 'flex-start' }}
                    >
                      Sign in with email & password
                    </Button>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => {
                        setError(null);
                        setShowEmailSignup(true);
                      }}
                      sx={{ justifyContent: 'flex-start', textDecoration: 'underline' }}
                    >
                      Create account with different email
                    </Button>
                  </Box>
                </Box>
              )}
            </Alert>
          )}

          {!showEmailSignup ? (
            <>
              {/* Google Sign-Up Button - Highlighted */}
              <Box sx={{ 
                p: 2, 
                border: '2px solid', 
                borderColor: 'primary.main', 
                borderRadius: 2, 
                bgcolor: 'primary.lighter',
                mb: 2 
              }}>
                <Typography variant="body2" color="primary.main" sx={{ mb: 1, fontWeight: 600 }}>
                  Recommended: Quick signup with Google
                </Typography>
                <GoogleSignInButton 
                  variant="sign-up" 
                  onError={handleGoogleError}
                  disabled={isLoading}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', my: 3 }}>
                <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
                <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                  or
                </Typography>
                <Box sx={{ flexGrow: 1, height: '1px', bgcolor: 'divider' }} />
              </Box>

              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="text"
                  onClick={() => setShowEmailSignup(true)}
                  sx={{ 
                    textDecoration: 'underline',
                    color: 'primary.main',
                    fontWeight: 500
                  }}
                >
                  Create account with email & password instead
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" color="primary.main">
                  Email Signup
                </Typography>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowEmailSignup(false)}
                  sx={{ minWidth: 'auto' }}
                >
                  ← Back to Google signup
                </Button>
              </Box>
            </>
          )}

          {showEmailSignup && (
            <Tabs 
              value={registerMethod} 
              onChange={(e, newValue) => setRegisterMethod(newValue)}
              sx={{ mb: 3 }}
            >
              <Tab label="Email" value="email" />
              <Tab label="Phone" value="phone" />
            </Tabs>
          )}

          {showEmailSignup && registerMethod === 'email' && (
            <Box component="form" onSubmit={handleEmailSubmit(onEmailSubmit)} noValidate>
              <TextField
                {...registerEmail('name', {
                  required: 'Name is required',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters'
                  }
                })}
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                autoFocus
                error={!!emailErrors.name}
                helperText={emailErrors.name?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerEmail('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Please enter a valid email address'
                  }
                })}
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                error={!!emailErrors.email}
                helperText={emailErrors.email?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerEmail('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                error={!!emailErrors.password}
                helperText={emailErrors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerEmail('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => {
                    const password = watchEmail('password');
                    return value === password || 'Passwords do not match';
                  }
                })}
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                error={!!emailErrors.confirmPassword}
                helperText={emailErrors.confirmPassword?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* reCAPTCHA */}
              {recaptchaEnabled && recaptchaSiteKey && (
                <Box sx={{ mt: 2 }}>
                  <ReCaptcha
                    siteKey={recaptchaSiteKey}
                    action="REGISTER"
                    onVerify={recaptcha.handleVerify}
                    onError={recaptcha.handleError}
                    disabled={isLoading}
                    autoExecute={true}
                  />
                  {recaptcha.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {recaptcha.error}
                    </Alert>
                  )}
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || (recaptchaEnabled && !recaptcha.isVerified)}
              >
                {emailRegisterMutation.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </Box>
          )}

          {showEmailSignup && registerMethod === 'phone' && (
            <Box component="form" onSubmit={handlePhoneSubmit(onPhoneSubmit)} noValidate>
              <TextField
                {...registerPhone('name', {
                  required: 'Name is required',
                  minLength: {
                    value: 2,
                    message: 'Name must be at least 2 characters'
                  }
                })}
                margin="normal"
                required
                fullWidth
                id="name"
                label="Full Name"
                name="name"
                autoComplete="name"
                autoFocus
                error={!!phoneErrors.name}
                helperText={phoneErrors.name?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerPhone('phoneNumber', {
                  required: 'Phone number is required',
                  validate: (value) => {
                    const validation = validatePhoneNumber(value);
                    return validation.isValid || validation.error || 'Invalid phone number';
                  }
                })}
                margin="normal"
                required
                fullWidth
                id="phoneNumber"
                label="Phone Number"
                name="phoneNumber"
                autoComplete="tel"
                error={!!phoneErrors.phoneNumber}
                helperText={phoneErrors.phoneNumber?.message || 'Enter with country code (e.g., +1 for US)'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerPhone('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                error={!!phoneErrors.password}
                helperText={phoneErrors.password?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                {...registerPhone('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => {
                    const password = watchPhone('password');
                    return value === password || 'Passwords do not match';
                  }
                })}
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                error={!!phoneErrors.confirmPassword}
                helperText={phoneErrors.confirmPassword?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {/* reCAPTCHA for phone registration */}
              {recaptchaEnabled && recaptchaSiteKey && (
                <Box sx={{ mt: 2 }}>
                  <ReCaptcha
                    siteKey={recaptchaSiteKey}
                    action="REGISTER"
                    onVerify={recaptcha.handleVerify}
                    onError={recaptcha.handleError}
                    disabled={isLoading}
                    autoExecute={true}
                  />
                  {recaptcha.error && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {recaptcha.error}
                    </Alert>
                  )}
                </Box>
              )}

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || (recaptchaEnabled && !recaptcha.isVerified)}
              >
                {phoneRegisterMutation.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Create Account'
                )}
              </Button>
            </Box>
          )}

          {/* Enhanced Phone Registration Flow */}
          {showEmailSignup && !showPhoneFlow && (
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setShowPhoneFlow(true)}
                disabled={isLoading}
                sx={{ textTransform: 'none' }}
              >
                Use SMS-only registration
              </Button>
            </Box>
          )}

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <MuiLink component={Link} to="/auth/login" sx={{ textDecoration: 'none' }}>
                Sign in here
              </MuiLink>
            </Typography>
          </Box>
        </Paper>

        {/* Enhanced Phone Registration Flow */}
        {showPhoneFlow && (
          <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%', mt: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom align="center">
              SMS Registration
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
              Create your account using only your phone number with SMS verification
            </Typography>

            <PhoneAuthFlow
              mode="register"
              onSuccess={handlePhoneFlowSuccess}
              onError={handlePhoneFlowError}
              isLoading={isLoading}
              disabled={isLoading}
            />

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button
                variant="text"
                onClick={() => setShowPhoneFlow(false)}
                disabled={isLoading}
                color="inherit"
              >
                Back to regular registration
              </Button>
            </Box>
          </Paper>
        )}
      </Box>
    </Layout>
  );
}